// Funnel tracking for signup → checkout → purchase.
//
// One call per funnel step fans out to every destination we care about, so the
// use cases never talk to a vendor directly:
//
//   dataLayer (GTM)   every event, GA4-style name + `ecommerce` payload, plus
//                     `user_data` for enhanced conversions. GTM is the loader
//                     for GA4 / Google Ads, so this is their input.
//   Meta Pixel        `fbq` when present (GTM-loaded or self-managed via
//                     REACT_APP_FB_PIXEL_ID). Standard events get `track`,
//                     the rest `trackCustom`. Every call carries an `eventID`
//                     that is also sent to our server event, so the Conversions
//                     API can dedupe against the browser hit.
//   tracking.scaleup  server-side event (see track.ts) for the conversions
//                     that matter to ads: Lead, CompleteRegistration,
//                     InitiateCheckout, Purchase, PurchasePending, failures.
//   HubSpot/Clarity   through the legacy `trackUser`, keeping the event names
//                     the CRM already knows.
//
// Purchase only fires for a paid, confirmed order (TrialReturn). Free accounts
// fire CompleteRegistration + FreeAccountActivated (Meta `StartTrial`), so the
// Purchase event stays clean for value-based optimisation.
import trackUser, {
  trackingEventSmartplayer,
  getConversionLink,
  getFacebookFBC,
  getFacebookFBP,
} from './track';

declare global {
  interface Window {
    dataLayer: any[];
    _fbq?: any;
    smartplayerOnboardingSubmit?: (payload?: unknown) => void;
  }
}

export type FunnelEventName =
  | 'SignupView'
  | 'SignupEmailSubmitted'
  | 'SignupDataView'
  | 'Lead'
  | 'SignupConfirmationView'
  | 'SignupCodeResent'
  | 'SignupCodeInvalid'
  | 'CompleteRegistration'
  | 'FreeAccountActivated'
  | 'CheckoutView'
  | 'InitiateCheckout'
  | 'CheckoutRedirect'
  | 'CheckoutError'
  | 'CheckoutReturnView'
  | 'CheckoutCanceled'
  | 'PurchasePending'
  | 'Purchase'
  | 'PurchaseFail'
  | 'PurchaseTimeout'
  | 'PasswordCreated'
  | 'OnboardingView'
  | 'OnboardingComplete'
  | 'Login';

export interface FunnelPlan {
  code: string;
  name?: string;
  price?: number;
  recurring?: boolean;
  monthsValidity?: number;
}

export interface FunnelParams {
  /** Plan involved in the step (selected / bought). */
  plan?: FunnelPlan;
  /** Plans shown (CheckoutView). */
  plans?: FunnelPlan[];
  /** Overrides plan.price for value-based events. */
  value?: number;
  currency?: string;
  /** Chargefy session id → GA4 transaction_id / Meta order_id. */
  transactionId?: string;
  method?: 'email' | 'google';
  /** Error key / status that explains a failure or pending state. */
  reason?: string;
  status?: string;
  origin?: string;
  /** Fire at most once per browser session for this key (e.g. purchase-<sessionId>). */
  once?: string;
}

export interface FunnelUser {
  email?: string;
  phone?: string;
  name?: string;
  externalId?: string;
}

interface EventSpec {
  /** Meta standard event name; omitted → trackCustom with the funnel name. */
  meta?: string;
  /** Extra Meta standard event fired alongside (Purchase → Subscribe). */
  metaAlso?: (params: FunnelParams) => string | null;
  ga4: string;
  /** Also send the server-side event (tracking.scaleup.com.br). */
  server?: boolean;
  /** Legacy name/type for trackUser (HubSpot / Clarity). */
  legacy?: {type: 'track' | 'trackCustom'; name: string};
}

const FUNNEL_STEP: Record<FunnelEventName, number> = {
  SignupView: 1,
  SignupEmailSubmitted: 2,
  SignupDataView: 3,
  Lead: 4,
  SignupConfirmationView: 5,
  SignupCodeResent: 5,
  SignupCodeInvalid: 5,
  CompleteRegistration: 6,
  FreeAccountActivated: 6,
  CheckoutView: 7,
  InitiateCheckout: 9,
  CheckoutRedirect: 10,
  CheckoutError: 10,
  CheckoutReturnView: 11,
  CheckoutCanceled: 11,
  PurchasePending: 12,
  Purchase: 13,
  PurchaseFail: 13,
  PurchaseTimeout: 13,
  PasswordCreated: 14,
  OnboardingView: 15,
  OnboardingComplete: 16,
  Login: 0,
};

const SPECS: Record<FunnelEventName, EventSpec> = {
  SignupView: {meta: 'ViewContent', ga4: 'signup_view', legacy: {type: 'trackCustom', name: 'RegistrationView'}},
  SignupEmailSubmitted: {ga4: 'signup_email_submitted'},
  SignupDataView: {ga4: 'signup_data_view', legacy: {type: 'trackCustom', name: 'RegistrationData'}},
  Lead: {meta: 'Lead', ga4: 'generate_lead', server: true, legacy: {type: 'track', name: 'Lead'}},
  SignupConfirmationView: {
    ga4: 'signup_confirmation_view',
    legacy: {type: 'trackCustom', name: 'RegistrationConfirmation'},
  },
  SignupCodeResent: {ga4: 'signup_code_resent'},
  SignupCodeInvalid: {ga4: 'signup_code_invalid'},
  CompleteRegistration: {
    meta: 'CompleteRegistration',
    ga4: 'sign_up',
    server: true,
    legacy: {type: 'trackCustom', name: 'RegistrationConfirmationDone'},
  },
  FreeAccountActivated: {meta: 'StartTrial', ga4: 'free_account_activated', server: true},
  // The plan comes from the website (?plan=): the checkout page shows one plan, so GA4 view_item.
  CheckoutView: {meta: 'ViewContent', ga4: 'view_item', legacy: {type: 'trackCustom', name: 'TrialView'}},
  InitiateCheckout: {meta: 'InitiateCheckout', ga4: 'begin_checkout', server: true},
  CheckoutRedirect: {ga4: 'checkout_redirect'},
  CheckoutError: {ga4: 'checkout_error', server: true, legacy: {type: 'trackCustom', name: 'CheckoutError'}},
  CheckoutReturnView: {ga4: 'checkout_return_view'},
  CheckoutCanceled: {ga4: 'checkout_canceled'},
  PurchasePending: {meta: 'AddPaymentInfo', ga4: 'add_payment_info', server: true},
  Purchase: {
    meta: 'Purchase',
    metaAlso: params => (params.plan?.recurring ? 'Subscribe' : null),
    ga4: 'purchase',
    server: true,
    legacy: {type: 'track', name: 'Purchase'},
  },
  PurchaseFail: {ga4: 'purchase_fail', server: true, legacy: {type: 'trackCustom', name: 'PurchaseFail'}},
  PurchaseTimeout: {ga4: 'purchase_timeout'},
  PasswordCreated: {ga4: 'password_created'},
  OnboardingView: {ga4: 'onboarding_view'},
  OnboardingComplete: {ga4: 'onboarding_complete'},
  Login: {ga4: 'login', legacy: {type: 'track', name: 'Login'}},
};

const USER_KEY = 'funnel-user';
const ONCE_PREFIX = 'funnel-once-';
const DEFAULT_CURRENCY = 'BRL';
const PIXEL_ID = process.env.REACT_APP_FB_PIXEL_ID || '507676058791497';

// ---------------------------------------------------------------------------
// identity & attribution
// ---------------------------------------------------------------------------

function safeJson<T>(raw: string | null, fallback: T): T {
  try {
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

/** Email/phone/name the signup flow already keeps in sessionStorage, merged with identify() calls. */
export function getFunnelUser(): FunnelUser {
  const s = storage();
  const saved = safeJson<FunnelUser>(s?.getItem(USER_KEY) || null, {});

  return {
    email: saved.email || s?.getItem('signup-user-email') || undefined,
    phone: saved.phone || s?.getItem('signup-user-phone') || undefined,
    name: saved.name || s?.getItem('signup-user-name') || undefined,
    externalId: saved.externalId,
  };
}

/** Remember who the visitor is (used for Meta advanced matching and GA4 user_data). */
export function identifyFunnelUser(user: FunnelUser): void {
  const s = storage();
  const merged: FunnelUser = {...getFunnelUser()};

  (Object.keys(user) as (keyof FunnelUser)[]).forEach(key => {
    if (user[key]) {
      merged[key] = user[key];
    }
  });

  s?.setItem(USER_KEY, JSON.stringify(merged));
  applyAdvancedMatching(merged);
}

export function normalizeEmail(email?: string): string | undefined {
  const value = (email || '').trim().toLowerCase();
  return value || undefined;
}

/** Digits only, with Brazil's country code when the number looks national. */
export function normalizePhone(phone?: string): string | undefined {
  const raw = (phone || '').trim();
  const international = raw.startsWith('+');
  let digits = raw.replace(/\D/g, '');

  if (!digits) {
    return undefined;
  }

  // A "+" means the caller already gave the country code.
  if (!international && (digits.length === 10 || digits.length === 11) && !digits.startsWith('55')) {
    digits = `55${digits}`;
  }

  return digits;
}

export function splitName(name?: string): {firstName?: string; lastName?: string} {
  const parts = (name || '').trim().toLowerCase().split(/\s+/).filter(Boolean);

  if (parts.length === 0) {
    return {};
  }

  return {firstName: parts[0], lastName: parts.length > 1 ? parts.slice(1).join(' ') : undefined};
}

interface Attribution {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_term: string;
  utm_content: string;
  fbclid: string;
  fbp: string;
  fbc: string;
  landing_url: string;
}

function getAttribution(): Attribution {
  const utm = safeJson<Record<string, string | undefined>>(storage()?.getItem('utm') || null, {});

  return {
    utm_source: utm.utm_source || '',
    utm_medium: utm.utm_medium || '',
    utm_campaign: utm.utm_campaign || '',
    utm_term: utm.utm_term || '',
    utm_content: utm.utm_content || '',
    fbclid: utm.fbclid || '',
    fbp: getFacebookFBP() || '',
    fbc: getFacebookFBC() || '',
    landing_url: getConversionLink(),
  };
}

function newEventId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    // fall through
  }

  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

function resolveValue(params: FunnelParams): number {
  if (typeof params.value === 'number') {
    return params.value;
  }

  return typeof params.plan?.price === 'number' ? params.plan.price : 0;
}

// ---------------------------------------------------------------------------
// destinations
// ---------------------------------------------------------------------------

function pushDataLayer(name: FunnelEventName, spec: EventSpec, params: FunnelParams, eventId: string): void {
  try {
    window.dataLayer = window.dataLayer || [];

    const user = getFunnelUser();
    const {firstName, lastName} = splitName(user.name);
    const items = (params.plans && params.plans.length ? params.plans : params.plan ? [params.plan] : []).map(
      plan => ({
        item_id: plan.code,
        item_name: plan.name || plan.code,
        price: plan.price ?? 0,
        quantity: 1,
        item_category: plan.recurring ? 'subscription' : 'one_time',
      }),
    );

    // GA4 asks for the previous ecommerce object to be cleared first.
    window.dataLayer.push({ecommerce: null});
    window.dataLayer.push({
      event: spec.ga4,
      event_id: eventId,
      funnel_event: name,
      funnel_step: FUNNEL_STEP[name],
      method: params.method || undefined,
      plan_code: params.plan?.code || undefined,
      reason: params.reason || undefined,
      status: params.status || undefined,
      origin: params.origin || undefined,
      ...getAttribution(),
      user_data: {
        email: normalizeEmail(user.email),
        phone_number: normalizePhone(user.phone),
        first_name: firstName,
        last_name: lastName,
        external_id: user.externalId,
      },
      ecommerce: {
        currency: params.currency || DEFAULT_CURRENCY,
        value: resolveValue(params),
        transaction_id: params.transactionId || undefined,
        items,
      },
    });
  } catch (e) {
    console.log('Problem to push dataLayer', e);
  }
}

function metaParams(name: FunnelEventName, params: FunnelParams): Record<string, unknown> {
  const codes = (params.plans && params.plans.length ? params.plans : params.plan ? [params.plan] : []).map(
    plan => plan.code,
  );
  const value = resolveValue(params);

  const data: Record<string, unknown> = {
    content_name: params.plan?.name || params.plan?.code || name,
    content_category: 'smartplayer',
    currency: params.currency || DEFAULT_CURRENCY,
    value,
    funnel_step: FUNNEL_STEP[name],
  };

  if (codes.length) {
    data.content_ids = codes;
    data.content_type = 'product';
    data.num_items = params.plan ? 1 : codes.length;
  }

  if (params.transactionId) {
    data.order_id = params.transactionId;
  }

  if (params.plan?.recurring && name === 'Purchase') {
    // Monthly price × 12 is a fair first-year LTV for the Meta value optimizer.
    data.predicted_ltv = params.plan.monthsValidity === 1 ? value * 12 : value;
  }

  if (params.method) {
    data.method = params.method;
  }

  if (params.reason) {
    data.reason = params.reason;
  }

  if (params.status) {
    data.status = params.status;
  }

  return data;
}

// Events fired before the (GTM-loaded) pixel exists are held here and replayed
// as soon as `window.fbq` shows up — the first steps (SignupView, CheckoutView)
// run on mount, usually before gtm.js has injected fbevents.js.
const META_READY_POLL_MS = 250;
const META_READY_MAX_WAIT_MS = 15000;
const pendingMeta: Array<() => void> = [];
let metaReadyTimer: ReturnType<typeof setInterval> | null = null;

function flushPendingMeta(): void {
  if (!window.fbq) {
    return;
  }

  while (pendingMeta.length) {
    const send = pendingMeta.shift();

    try {
      send?.();
    } catch (e) {
      console.log('Problem to track fb', e);
    }
  }
}

function waitForMeta(): void {
  if (metaReadyTimer) {
    return;
  }

  const startedAt = Date.now();

  metaReadyTimer = setInterval(() => {
    if (window.fbq) {
      clearInterval(metaReadyTimer as ReturnType<typeof setInterval>);
      metaReadyTimer = null;
      flushPendingMeta();
      return;
    }

    if (Date.now() - startedAt > META_READY_MAX_WAIT_MS) {
      clearInterval(metaReadyTimer as ReturnType<typeof setInterval>);
      metaReadyTimer = null;
      pendingMeta.length = 0;
    }
  }, META_READY_POLL_MS);
}

function sendMeta(name: FunnelEventName, spec: EventSpec, params: FunnelParams, eventId: string): void {
  try {
    if (!window.fbq) {
      pendingMeta.push(() => sendMeta(name, spec, params, eventId));
      waitForMeta();
      return;
    }

    // Keep order: anything queued while the pixel was missing goes out first.
    flushPendingMeta();

    const data = metaParams(name, params);

    if (spec.meta) {
      window.fbq('track', spec.meta, data, {eventID: eventId});
    } else {
      window.fbq('trackCustom', name, data, {eventID: eventId});
    }

    const also = spec.metaAlso?.(params);

    if (also) {
      window.fbq('track', also, data, {eventID: `${eventId}-${also.toLowerCase()}`});
    }
  } catch (e) {
    console.log('Problem to track fb', e);
  }
}

function sendServer(name: FunnelEventName, params: FunnelParams, eventId: string): void {
  const user = getFunnelUser();
  const attribution = getAttribution();

  // Fire-and-forget: the server call does an IP lookup and must not hold the UI.
  trackingEventSmartplayer(name, {
    email: user.email || '',
    phone: user.phone || '',
    name: user.name || '',
    plan: params.plan?.code || '',
    value: resolveValue(params),
    origin: params.origin || params.method || '',
    utmSource: attribution.utm_source,
    utmMedium: attribution.utm_medium,
    utmCampaign: attribution.utm_campaign,
    utmTerm: attribution.utm_term,
    utmContent: attribution.utm_content,
    eventId,
    currency: params.currency || DEFAULT_CURRENCY,
    transactionId: params.transactionId || '',
    status: params.status || params.reason || '',
  }).catch(() => undefined);
}

function sendLegacy(spec: EventSpec, params: FunnelParams): void {
  if (!spec.legacy) {
    return;
  }

  const user = getFunnelUser();
  const attribution = getAttribution();

  trackUser(
    spec.legacy.type,
    spec.legacy.name,
    {
      email: user.email,
      name: user.name,
      phone: user.phone,
      plan: params.plan?.code,
      value: resolveValue(params),
      currency: params.currency || DEFAULT_CURRENCY,
      utm_source: attribution.utm_source,
      utm_medium: attribution.utm_medium,
      utm_campaign: attribution.utm_campaign,
      utm_term: attribution.utm_term,
      utm_content: attribution.utm_content,
      fbclid: attribution.fbclid,
    },
    // GA4 already received this event through the dataLayer push.
    {skipGtag: true},
  );
}

// ---------------------------------------------------------------------------
// public API
// ---------------------------------------------------------------------------

/** Tracks one funnel step everywhere. Returns the event id (shared with the server event), or null when deduped. */
export function trackFunnel(name: FunnelEventName, params: FunnelParams = {}): string | null {
  const spec = SPECS[name];
  const s = storage();

  if (params.once) {
    const key = `${ONCE_PREFIX}${params.once}`;

    if (s?.getItem(key)) {
      return null;
    }

    s?.setItem(key, '1');
  }

  const eventId = newEventId();

  pushDataLayer(name, spec, params, eventId);
  sendMeta(name, spec, params, eventId);
  sendLegacy(spec, params);

  if (spec.server) {
    sendServer(name, params, eventId);
  }

  return eventId;
}

// ---------------------------------------------------------------------------
// bootstrap: Meta Pixel (optional self-managed) + _fbc cookie
// ---------------------------------------------------------------------------

function cookieDomain(): string | undefined {
  const host = window.location.hostname;

  if (host.endsWith('scaleup.com.br')) {
    return 'scaleup.com.br';
  }

  if (host.endsWith('smartplayer.ai')) {
    return 'smartplayer.ai';
  }

  return undefined;
}

/**
 * Meta only sets `_fbc` itself when the pixel loads on the landing page. The
 * signup page is often that landing (ads → /auth/criar-conta?fbclid=…), so we
 * write it ourselves in Meta's format when it is missing. CAPI matching
 * depends on it.
 */
export function ensureFbcCookie(): void {
  try {
    const fbclid = new URLSearchParams(window.location.search).get('fbclid');

    if (!fbclid || document.cookie.split(';').some(c => c.trim().startsWith('_fbc='))) {
      return;
    }

    const domain = cookieDomain();
    const maxAge = 90 * 24 * 60 * 60;
    document.cookie =
      `_fbc=fb.1.${Date.now()}.${fbclid}; path=/; max-age=${maxAge}; SameSite=Lax` +
      (domain ? `; domain=${domain}` : '') +
      (window.location.protocol === 'https:' ? '; Secure' : '');
  } catch {
    // cookies disabled
  }
}

function advancedMatching(user: FunnelUser): Record<string, string> {
  const {firstName, lastName} = splitName(user.name);
  const data: Record<string, string> = {};
  const em = normalizeEmail(user.email);
  const ph = normalizePhone(user.phone);

  if (em) data.em = em;
  if (ph) data.ph = ph;
  if (firstName) data.fn = firstName;
  if (lastName) data.ln = lastName;
  if (user.externalId) data.external_id = user.externalId;

  return data;
}

let pixelSelfManaged = false;

function applyAdvancedMatching(user: FunnelUser): void {
  try {
    if (!pixelSelfManaged || !window.fbq || !PIXEL_ID) {
      return;
    }

    // Re-init with the same id is how Meta documents updating advanced matching.
    window.fbq('init', PIXEL_ID, advancedMatching(user));
  } catch (e) {
    console.log('Problem to update fb advanced matching', e);
  }
}

function loadPixel(): void {
  if (window.fbq || !PIXEL_ID) {
    return;
  }

  // Standard Meta Pixel base code, inlined so the id can come from env.
  const fbq: any = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod.apply(fbq, args);
    } else {
      fbq.queue.push(args);
    }
  };

  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = '2.0';
  fbq.queue = [];
  window.fbq = fbq;
  window._fbq = window._fbq || fbq;

  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);

  pixelSelfManaged = true;
  window.fbq('init', PIXEL_ID, advancedMatching(getFunnelUser()));
  window.fbq('track', 'PageView');

  // SPA navigation: fire PageView on every route change.
  const pushState = window.history.pushState.bind(window.history);
  window.history.pushState = (...args: Parameters<History['pushState']>) => {
    pushState(...args);
    try {
      window.fbq('track', 'PageView');
    } catch {
      // ignore
    }
  };
  window.addEventListener('popstate', () => {
    try {
      window.fbq('track', 'PageView');
    } catch {
      // ignore
    }
  });
}

/** Call once at app start (src/index.tsx). */
export function initFunnelTracking(): void {
  try {
    window.dataLayer = window.dataLayer || [];
    ensureFbcCookie();
    loadPixel();
  } catch (e) {
    console.log('Problem to init funnel tracking', e);
  }
}
